import angular from 'angular';
import { noop } from 'lodash';
import * as call from '../../../../modules/call';
import { AngularServices } from '../../../../modules/core';
import { VideoProviderEvents } from '../../../../providers/video/common/video-provider-events';
import { RootScopeEvents } from '../../../../common/services/root-scope-types';
export class PiPService {
    /* @ngInject */
    constructor(VideoProviderFactory, activeStreamService, Browser, notificationFactory, $rootScope, $ngRedux, $log) {
        this.VideoProviderFactory = VideoProviderFactory;
        this.activeStreamService = activeStreamService;
        this.Browser = Browser;
        this.notificationFactory = notificationFactory;
        this.$rootScope = $rootScope;
        this.$ngRedux = $ngRedux;
        this.$log = $log;
        this.PIP_VIDEO_ID = 'pip-video';
        this.deregisterPhoneStopListener = () => noop;
        this.deregisterDoRestartListener = () => noop;
        this.updatePiP = this._updatePiP.bind(this);
        this.onDisconnect = this._onDisconnect.bind(this);
    }
    initPiP() {
        this.$ngRedux.dispatch(call.actions.setIsPiPTurnedOn());
        this.activeStreamService.on('update', this.updatePiP);
        this.VideoProviderFactory.activeProvider.on(VideoProviderEvents.SessionDisconnected, this.onDisconnect);
        this.deregisterPhoneStopListener = this.$rootScope.$on(RootScopeEvents.COMMUNICATION_CONTROL.PHONE_STOP, this.onDisconnect);
        this.deregisterDoRestartListener = this.$rootScope.$on(RootScopeEvents.COMMUNICATION_CONTROL.DO_RESTART, this.onDisconnect);
    }
    async destroyPiP() {
        await this.removePiPVideoEl();
        this.$ngRedux.dispatch(call.actions.setIsPiPTurnedOff());
        this.activeStreamService.off('update', this.updatePiP);
        this.VideoProviderFactory.activeProvider.off(VideoProviderEvents.SessionDisconnected, this.onDisconnect);
        this.deregisterPhoneStopListener();
        this.deregisterDoRestartListener();
    }
    get isPiPSupported() {
        return document.pictureInPictureEnabled;
    }
    get isStreamSuitableForPiP() {
        return !['self', 'screen'].includes(this.activeStreamService.getCurrentStreamId());
    }
    get PiPVideoEl() {
        return document.querySelector(`#${this.PIP_VIDEO_ID}`);
    }
    isNoVideoTrack(videoEl) {
        return videoEl.srcObject.getVideoTracks().length === 0;
    }
    isPiPVideo(videoEl) {
        return (videoEl !== undefined && videoEl === document.pictureInPictureElement);
    }
    async getActiveVideoEl() {
        return this.activeStreamService.getCurrentVideoElement();
    }
    showNotification() {
        this.notificationFactory.showCommon('warning', 'picture-in-picture.warning');
    }
    async createPiPVideoEl(stream) {
        const video = document.createElement('video');
        video.id = this.PIP_VIDEO_ID;
        video.playsInline = true;
        video.muted = true;
        video.style.cssText =
            'width: 1px; height: 1px; top:0; left:0; z-index: 9999; position: absolute';
        video.srcObject = new MediaStream(stream);
        await video.play();
        document.body.append(video);
        video.addEventListener(call.constants.HTMLMediaElementEvents.LeavePictureInPicture, this.onDisconnect, { once: true });
        return video;
    }
    async togglePiP() {
        if (!this.isPiPSupported) {
            return;
        }
        if (document.pictureInPictureElement) {
            await this.destroyPiP();
            return;
        }
        if (!this.isStreamSuitableForPiP) {
            return;
        }
        const activeVideoEl = await this.getActiveVideoEl();
        if (!activeVideoEl?.srcObject) {
            this.showNotification();
            return;
        }
        if (this.Browser.isSafari()) {
            await this.enablePiP(activeVideoEl);
            return;
        }
        await this.createPiPVideoEl(activeVideoEl.srcObject);
        await this.enablePiP();
    }
    async enablePiP(videoEl) {
        const pipVideoEl = videoEl ?? this.PiPVideoEl;
        if (this.isPiPVideo(pipVideoEl)) {
            return;
        }
        if (this.isNoVideoTrack(pipVideoEl)) {
            this.showNotification();
            return;
        }
        await this.disablePiP();
        try {
            await pipVideoEl.requestPictureInPicture();
            pipVideoEl.addEventListener(call.constants.HTMLMediaElementEvents.LeavePictureInPicture, this.onDisconnect, { once: true });
            this.initPiP();
        }
        catch (err) {
            this.$log.error(err.message);
        }
        this.$rootScope.$emit(RootScopeEvents.PICTURE_IN_PICTURE.START);
    }
    async _updatePiP() {
        if (!this.isStreamSuitableForPiP || !this.PiPVideoEl) {
            return;
        }
        const activeVideoEl = await this.getActiveVideoEl();
        if (!activeVideoEl?.srcObject) {
            return;
        }
        const stream = this.PiPVideoEl.srcObject;
        if (stream) {
            for (const track of stream.getVideoTracks()) {
                stream.removeTrack(track);
            }
            for (const track of activeVideoEl.srcObject.getVideoTracks()) {
                stream.addTrack(track);
            }
        }
        else {
            this.PiPVideoEl.srcObject = new MediaStream(activeVideoEl.srcObject);
            await this.PiPVideoEl.play();
        }
    }
    async disablePiP() {
        if (document.pictureInPictureElement) {
            try {
                await document.exitPictureInPicture();
            }
            catch (err) {
                this.$log.error(err.message);
            }
        }
    }
    async removePiPVideoEl() {
        await this.disablePiP();
        if (this.PiPVideoEl) {
            this.PiPVideoEl.pause();
            this.PiPVideoEl.srcObject = null;
            this.PiPVideoEl.remove();
        }
    }
    async _onDisconnect() {
        return this.destroyPiP();
    }
}
export default angular
    .module('doxy.videoChat.common.services.PiPService', [])
    .service(AngularServices.PiPService, PiPService).name;
